/**
 * dataSourceHelpers.js
 *
 * Utility functions for managing data sources (S2P, Database, Manual)
 * Used by UI components to determine available data sources and format labels
 */

/**
 * Get available data sources for a specific parameter
 * @param {Object} node - The node object containing parameters
 * @param {string} paramName - Parameter name ('gain', 'noiseFigure', 'p1db', 'oip3')
 * @param {Object} componentLibrary - Optional component library to check for S2P availability
 * @returns {Array} Array of available data sources [{value, label}, ...]
 */
export function getAvailableDataSources(node, paramName, componentLibrary = null) {
  if (!node || !node.data || !node.data.parameters) {
    return [{ value: 'manual', label: 'Manual' }];
  }

  const params = node.data.parameters;
  const sources = [];

  // S2P only provides Gain (S21 magnitude)
  // Check both node parameter AND component library (library may be updated after node was created)
  const nodeHasS2P = params.hasS2PData?.value;
  const libraryHasS2P = componentLibrary && node.type && componentLibrary[node.type]?.hasS2P;
  if (paramName === 'gain' && (nodeHasS2P || libraryHasS2P)) {
    sources.push({ value: 's2p', label: 'S2P' });
  }

  // Database provides Gain, NF, P1dB, OIP3
  if (params.hasFreqData?.value &&
      ['gain', 'noiseFigure', 'p1db', 'oip3'].includes(paramName)) {
    sources.push({
      value: 'database',
      label: 'Database'
    });
  }

  // Manual always available
  sources.push({ value: 'manual', label: 'Manual' });

  return sources;
}

/**
 * Get the current data source for a parameter
 * @param {Object} node - The node object
 * @param {string} paramName - Parameter name ('gain', 'noiseFigure', 'p1db', 'oip3')
 * @returns {string} Current data source ('s2p', 'database', or 'manual')
 */
export function getCurrentDataSource(node, paramName) {
  if (!node || !node.data || !node.data.parameters) {
    return 'manual';
  }

  const params = node.data.parameters;

  // Map parameter names to data source parameter keys
  const dataSourceKeyMap = {
    'gain': 'gainDataSource',
    'noiseFigure': 'nfDataSource',
    'p1db': 'p1dbDataSource',
    'oip3': 'oip3DataSource'
  };

  const dataSourceKey = dataSourceKeyMap[paramName];
  if (!dataSourceKey) {
    return 'manual';
  }

  return params[dataSourceKey]?.value || 'manual';
}

/**
 * Check if a node has S2P data available
 * @param {Object} node - The node object
 * @returns {boolean} True if S2P data is available
 */
export function hasS2PData(node) {
  return node?.data?.parameters?.hasS2PData?.value === true;
}

/**
 * Check if a node has frequency database data available
 * @param {Object} node - The node object
 * @returns {boolean} True if frequency database data is available
 */
export function hasFreqData(node) {
  return node?.data?.parameters?.hasFreqData?.value === true;
}

/**
 * Get the display label for a data source
 * @param {string} source - Data source ('s2p', 'database', 'manual')
 * @param {number} voltage - Optional voltage for database source
 * @returns {string} Display label
 */
export function getDataSourceLabel(source, voltage = null) {
  switch (source) {
    case 's2p':
      return 'S2P';
    case 'database':
      return voltage ? `Database (${voltage}V)` : 'Database';
    case 'manual':
      return 'Manual';
    default:
      return 'Manual';
  }
}

/**
 * Check if a parameter supports data source selection
 * @param {string} paramName - Parameter name
 * @returns {boolean} True if the parameter supports data sources
 */
export function supportsDataSource(paramName) {
  return ['gain', 'noiseFigure', 'p1db', 'oip3'].includes(paramName);
}

/**
 * Map parameter keys to display names
 * @param {string} paramKey - Parameter key (e.g., 'gain', 'noiseFigure')
 * @returns {string} Display name (e.g., 'Gain', 'Noise Figure')
 */
export function getParameterDisplayName(paramKey) {
  const nameMap = {
    'gain': 'Gain',
    'noiseFigure': 'Noise Figure',
    'p1db': 'OP1dB',
    'oip3': 'OIP3'
  };
  return nameMap[paramKey] || paramKey;
}
